import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  User, 
  Calendar,
  Filter,
  Search,
  Plus,
  ChevronDown,
  AlertCircle,
  Loader2,
  Star
} from 'lucide-react';
import Button from '@components/common/Button';
import mentorService from '@services/mentor.service';
import { ROUTES } from '@constants';
import toast from 'react-hot-toast';

const MyQuestions = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, answered
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await mentorService.getQuestionHistory();
      
      if (response.success) {
        const questionData = response.data?.data?.history || response.data?.history || [];
        setQuestions(Array.isArray(questionData) ? questionData : []);
      }
    } catch (error) {
      console.error('Failed to fetch question history:', error);
      toast.error('Failed to load question history');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const getMentorName = (question) => {
    if (question.mentorName) return question.mentorName;
    if (question.mentor?.firstName || question.mentor?.lastName) {
      return `${question.mentor?.firstName || ''} ${question.mentor?.lastName || ''}`.trim();
    }
    return 'Mentor';
  };

  const getMentorExpertise = (question) => (
    question.mentorExpertise || question.mentor?.expertise || 'Expert Mentor'
  );

  const getAskedAt = (question) => (
    question.askedAt || question.questionDate || question.createdAt
  );

  const getAnsweredAt = (question) => (
    question.answeredAt || question.answerDate || question.updatedAt
  );

  const isAnswered = (question) => (
    Boolean(question.answer) && ['answered', 'approved'].includes(question.status)
  );

  const filteredQuestions = questions.filter(q => {
    const mentorName = getMentorName(q);
    const matchesFilter = filter === 'all' || (filter === 'answered' ? isAnswered(q) : q.status === filter);
    const matchesSearch = (q.question || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mentorName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: questions.length,
    pending: questions.filter(q => q.status === 'pending').length,
    answered: questions.filter(q => isAnswered(q)).length
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-lg sm:text-xl font-bold text-primary-700">{stats.total}</p>
              <p className="text-xs text-gray-600">Total</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-50 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-lg sm:text-xl font-bold text-primary-700">{stats.pending}</p>
              <p className="text-xs text-gray-600">Pending</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div>
              <p className="text-lg sm:text-xl font-bold text-primary-700">{stats.answered}</p>
              <p className="text-xs text-gray-600">Answered</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions or mentors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-secondary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pending'
                  ? 'bg-secondary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('answered')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'answered'
                  ? 'bg-secondary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Answered
            </button>
          </div>

          {/* Ask New Question Button */}
          <Button
            variant="primary"
            onClick={() => navigate(ROUTES.STUDENT_MENTOR)}
            className="whitespace-nowrap"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ask New Question
          </Button>
        </div>
      </div>

      {/* Questions List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-secondary-500 animate-spin" />
        </div>
      ) : filteredQuestions.length > 0 ? (
        <div className="space-y-3">
          {filteredQuestions.map((question) => (
            <div
              key={question.id}
              className={`rounded-xl border shadow-sm hover:shadow-md transition-shadow ${
                isAnswered(question)
                  ? 'bg-green-50 border-green-100'
                  : 'bg-red-50 border-red-100'
              }`}
            >
              {/* Question Header */}
              <div className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        isAnswered(question)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {isAnswered(question) ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Answered
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                        )}
                      </span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-primary-700 mb-2 leading-relaxed">
                      {question.question}
                    </h3>
                  </div>
                  
                  <button
                    onClick={() => setExpandedQuestion(
                      expandedQuestion === question.id ? null : question.id
                    )}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        expandedQuestion === question.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Mentor Info */}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-white">
                        {getMentorName(question).charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-primary-700">{getMentorName(question)}</p>
                      <p className="text-xs text-gray-500">{getMentorExpertise(question)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 text-xs">
                    <Calendar className="w-3.5 h-3.5" />
                    Asked on {formatDate(getAskedAt(question))}
                  </div>
                </div>
              </div>

              {/* Answer Section (Expandable) */}
              {expandedQuestion === question.id && (
                <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50">
                  {isAnswered(question) ? (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h4 className="font-semibold text-primary-700">Answer</h4>
                        <span className="text-xs text-gray-500">
                          • {formatDate(getAnsweredAt(question))}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed mb-4">
                        {question.answer}
                      </p>
                      
                      {/* Rating Display */}
                      {question.rating && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 font-medium">Your Rating:</span>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= question.rating
                                      ? 'fill-yellow-400 text-yellow-400'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                              <span className="text-sm text-gray-600 ml-1">
                                ({question.rating}/5)
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-start gap-3 text-sm text-gray-600">
                      <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-700 mb-1">
                          Waiting for mentor response
                        </p>
                        <p className="text-xs">
                          Your mentor will answer your question soon. You'll be notified once they respond.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-primary-700 mb-2">
            {searchTerm || filter !== 'all' ? 'No questions found' : 'No questions yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Start asking questions to get guidance from expert mentors'
            }
          </p>
          {!searchTerm && filter === 'all' && (
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.STUDENT_MENTOR)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ask Your First Question
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default MyQuestions;
